import React, { useState, useEffect, useRef } from 'react';
import { X, FileText, PenTool, Printer, CheckCircle, AlertCircle, GripVertical, Info } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { poaAPI } from '../utils/api';

// Signature font styles
const SIGNATURE_FONTS = {
  cursive: { fontFamily: "'Brush Script MT', cursive", fontSize: '28px' },
  elegant: { fontFamily: "'Times New Roman', serif", fontStyle: 'italic', fontSize: '24px' },
  formal: { fontFamily: "'Times New Roman', serif", fontWeight: 'bold', fontStyle: 'italic', fontSize: '22px' },
  modern: { fontFamily: "'Helvetica', sans-serif", fontWeight: 'bold', fontStyle: 'italic', fontSize: '22px' },
  classic: { fontFamily: "'Courier New', monospace", fontStyle: 'italic', fontSize: '22px' }
};

// US States list
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming', 'District of Columbia'
];

// Entity Types
const ENTITY_TYPES = [
  'Limited Liability Company (LLC)',
  'Corporation (C-Corp)',
  'S Corporation (S-Corp)',
  'Partnership',
  'Limited Partnership (LP)',
  'Limited Liability Partnership (LLP)',
  'Sole Proprietorship',
  'Non-Profit Corporation',
  'Professional Corporation (PC)',
  'Other'
];

// High-risk payment processors list (alphabetically sorted)
const PAYMENT_PROCESSORS = [
  'Lucra', 'Paysafe', 'Durango Merchant Services', 'eMerchantBroker', 'PayKickstart',
  'Soar Payments', 'Host Merchant Services', 'PaymentCloud', 'SMB Global', 'Instabill',
  'Allied Wallet', 'CCBill', 'Epoch', 'Segpay', 'Verotel', 'Stripe', 'Square', 'PayPal',
  'Braintree', 'Adyen', 'Worldpay', 'First Data', 'Fiserv', 'Global Payments', 'TSYS',
  'Elavon', 'Heartland', 'Chase Paymentech', 'Wells Fargo Merchant Services',
  'Bank of America Merchant Services', 'Payline Data', 'National Processing', 'Bankcard USA',
  'Electronic Merchant Systems', 'iPayment', 'Priority Payment Systems', 'Helcim',
  'Dharma Merchant Services', 'Fattmerchant (Stax)', 'Payment Depot', 'CDGcommerce',
  'Pivotal Payments', 'Cayan (TSYS)', 'Moneris', 'Authorize.Net', 'NMI (Network Merchants)',
  'USAePay', 'Payvision', 'Checkout.com', '2Checkout (Verifone)', 'BlueSnap', 'FastSpring',
  'Paddle', 'Chargebee', 'Recurly', 'Payoneer', 'Skrill', 'Neteller', 'PingPong Payments',
  'Airwallex', 'Paystack', 'Flutterwave', 'Razorpay', 'Paytm',
].sort();

const LimitedPOAForm = ({ isOpen, onClose, caseData, onSubmitSuccess }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1); // 1: Fill form, 2: Review & Sign, 3: Success
  const [loading, setLoading] = useState(false);
  const signatureRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [signaturePosition, setSignaturePosition] = useState({ placed: false, x: 0, y: 0 });
  
  // Processor autocomplete state
  const [processorSuggestions, setProcessorSuggestions] = useState([]);
  const [showProcessorDropdown, setShowProcessorDropdown] = useState(false);
  const [customProcessors, setCustomProcessors] = useState([]);
  const processorInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    merchant_legal_name: '',
    merchant_dba: '',
    state_of_incorporation: '',
    entity_type: '',
    merchant_address: '',
    processor_name: '',
    mid_numbers: '',
    signer_name: '',
    signer_title: '',
    signer_email: '',
    signature_text: '',
    signature_font: 'cursive',
    signature_date: format(new Date(), 'yyyy-MM-dd'),
    effective_date: format(new Date(), 'yyyy-MM-dd')
  });

  const [agreed, setAgreed] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const autoSaveTimerRef = useRef(null);

  // Get all processors (built-in + custom)
  const getAllProcessors = () => {
    const allProcessors = [...PAYMENT_PROCESSORS, ...customProcessors];
    return [...new Set(allProcessors)].sort();
  };

  // Load existing draft on mount
  useEffect(() => {
    const loadDraft = async () => {
      if (caseData?.id) {
        try {
          const response = await poaAPI.getDraft(caseData.id);
          if (response.data && response.data.status === 'draft') {
            setFormData(prev => ({
              ...prev,
              ...response.data.form_data
            }));
            setLastSaved(response.data.last_saved_at);
            toast.info('Loaded your saved draft');
          }
        } catch (error) {
          // No draft exists, that's okay
        }
      }
    };
    loadDraft();
  }, [caseData?.id]);

  // Auto-save function
  const autoSave = async () => {
    if (!caseData?.id || step !== 1) return;
    
    setIsSaving(true);
    try {
      const response = await poaAPI.saveDraft({
        case_id: caseData.id,
        form_data: formData
      });
      setLastSaved(response.data.last_saved_at);
    } catch (error) {
      console.error('Auto-save failed:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Debounced auto-save on form changes
  useEffect(() => {
    if (step === 1 && caseData?.id) {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      autoSaveTimerRef.current = setTimeout(() => {
        autoSave();
      }, 3000);
    }
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [formData, step, caseData?.id]);

  // Auto-populate from case data
  useEffect(() => {
    if (caseData) {
      setFormData(prev => ({
        ...prev,
        merchant_legal_name: caseData.company_name || '',
        processor_name: caseData.processor_name || '',
        mid_numbers: caseData.mid_numbers || caseData.frozen_revenues?.[0]?.account_number || '',
        signer_name: caseData.client_name || user?.full_name || '',
        signer_email: caseData.client_email || user?.email || '',
        merchant_address: caseData.merchant_address || ''
      }));
    }
  }, [caseData, user]);

  // Auto-populate from user if not from case
  useEffect(() => {
    if (user && !formData.signer_name) {
      setFormData(prev => ({
        ...prev,
        signer_name: user.full_name || '',
        signer_email: user.email || ''
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle processor input change with autocomplete
  const handleProcessorChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, processor_name: value });
    
    if (value.length >= 1) {
      const allProcessors = getAllProcessors();
      const filtered = allProcessors.filter(p => 
        p.toLowerCase().includes(value.toLowerCase())
      );
      setProcessorSuggestions(filtered);
      setShowProcessorDropdown(true);
    } else {
      setProcessorSuggestions([]);
      setShowProcessorDropdown(false);
    }
  };

  // Select processor from dropdown
  const selectProcessor = (processor) => {
    setFormData({ ...formData, processor_name: processor });
    setShowProcessorDropdown(false);
    setProcessorSuggestions([]);
  };

  // Add custom processor if not in list
  const addCustomProcessor = () => {
    const processorName = formData.processor_name.trim();
    if (processorName && !getAllProcessors().includes(processorName)) {
      setCustomProcessors(prev => [...prev, processorName]);
      toast.success(`"${processorName}" added to your processor list`);
    }
    setShowProcessorDropdown(false);
  };

  // Handle clicking outside processor dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (processorInputRef.current && !processorInputRef.current.contains(e.target)) {
        setShowProcessorDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle N/A button click
  const setToNA = (fieldName) => {
    setFormData({ ...formData, [fieldName]: 'N/A' });
  };

  const handleSubmit = async () => {
    if (!agreed || !formData.signature_text) {
      toast.error('Please complete the signature and agreement');
      return;
    }

    setLoading(true);
    try {
      const response = await poaAPI.submitPOA({
        case_id: caseData?.id,
        form_data: formData
      });

      toast.success('Limited POA submitted successfully!');
      setStep(3);
      if (onSubmitSuccess) onSubmitSuccess(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit POA');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Drag and drop signature handlers
  const handleDragStart = (e) => {
    setIsDragging(true);
    e.dataTransfer.setData('text/plain', 'signature');
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const dropZone = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - dropZone.left;
    const y = e.clientY - dropZone.top;
    
    setSignaturePosition({ placed: true, x, y });
    toast.success('Signature placed! Review and confirm to submit.');
  };

  const isFormValid = () => {
    return formData.merchant_legal_name && 
           formData.state_of_incorporation && 
           formData.entity_type &&
           formData.merchant_address && 
           formData.signer_name &&
           formData.signer_title &&
           formData.signer_email;
  };

  // Format merchant name with DBA
  const getFullMerchantName = () => {
    if (formData.merchant_dba && formData.merchant_dba !== 'N/A' && formData.merchant_dba.trim()) {
      return `${formData.merchant_legal_name} d/b/a ${formData.merchant_dba}`;
    }
    return formData.merchant_legal_name;
  };

  // Format effective date for display
  const getFormattedDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'MMMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[95vh] overflow-y-auto my-4 print:max-w-none print:m-0 print:shadow-none">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex justify-between items-center z-10 print:hidden">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <FileText className="text-purple-600" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Limited Power of Attorney</h2>
              <p className="text-sm text-slate-600">Reserve Release AI, LLC - LPOA Document</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Auto-save indicator */}
            {step === 1 && (
              <div className="flex items-center gap-2 text-xs text-slate-500">
                {isSaving ? (
                  <>
                    <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                    <span>Saving...</span>
                  </>
                ) : lastSaved ? (
                  <>
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <span>Saved {format(new Date(lastSaved), 'h:mm a')}</span>
                  </>
                ) : null}
              </div>
            )}
            <button
              onClick={handlePrint}
              className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
              title="Print Document"
            >
              <Printer size={20} />
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Progress Steps - Hide on print */}
        <div className="px-6 py-4 border-b border-slate-200 print:hidden">
          <div className="flex items-center justify-center gap-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  step >= s ? 'bg-purple-600 text-white' : 'bg-slate-200 text-slate-600'
                }`}>
                  {step > s ? <CheckCircle size={16} /> : s}
                </div>
                <span className={`ml-2 text-sm ${step >= s ? 'text-purple-600 font-medium' : 'text-slate-500'}`}>
                  {s === 1 ? 'Fill Form' : s === 2 ? 'Review & Sign' : 'Complete'}
                </span>
                {s < 3 && <div className={`w-12 h-0.5 mx-4 ${step > s ? 'bg-purple-600' : 'bg-slate-200'}`} />}
              </div>
            ))}
          </div>
        </div>

        {/* Document Content */}
        <div className="p-6 md:p-8" id="poa-document">
          {/* Logo Header for Print */}
          <div className="text-center mb-6 print:mb-8">
            <img 
              src="/logo.png" 
              alt="Reserve Release AI" 
              className="h-16 mx-auto mb-2"
            />
          </div>

          {/* Document Title */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-slate-900">
              Limited Power of Attorney
            </h2>
          </div>

          {step === 1 && (
            <div className="space-y-6">
              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="text-sm text-blue-800 font-medium">Complete the LPOA Form</p>
                  <p className="text-sm text-blue-700">
                    This Limited Power of Attorney authorizes Reserve Release AI, LLC to act on your behalf for reserve recovery. Fields marked with * are required.
                  </p>
                </div>
              </div>

              {/* Effective Date */}
              <div className="bg-slate-50 rounded-lg p-6">
                <h3 className="font-semibold text-slate-900 mb-4">EFFECTIVE DATE</h3>
                <div className="max-w-xs">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Effective Date *
                  </label>
                  <input
                    type="date"
                    name="effective_date"
                    value={formData.effective_date}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                    required
                  />
                </div>
              </div>

              {/* Principal (Merchant) Information */}
              <div className="bg-slate-50 rounded-lg p-6">
                <h3 className="font-semibold text-slate-900 mb-4">1. PRINCIPAL (MERCHANT) INFORMATION</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Merchant Legal Name *
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="merchant_legal_name"
                        value={formData.merchant_legal_name}
                        onChange={handleChange}
                        placeholder="Enter legal business name"
                        className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setToNA('merchant_legal_name')}
                        className="px-3 py-2 text-xs font-medium text-slate-600 bg-slate-200 hover:bg-slate-300 rounded-lg"
                      >
                        N/A
                      </button>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      DBA (Doing Business As) - Optional
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="merchant_dba"
                        value={formData.merchant_dba}
                        onChange={handleChange}
                        placeholder="If applicable"
                        className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => setToNA('merchant_dba')}
                        className="px-3 py-2 text-xs font-medium text-slate-600 bg-slate-200 hover:bg-slate-300 rounded-lg"
                      >
                        N/A
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      State of Incorporation/Formation *
                    </label>
                    <select
                      name="state_of_incorporation"
                      value={formData.state_of_incorporation}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      required
                    >
                      <option value="">Select State</option>
                      {US_STATES.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Entity Type *
                    </label>
                    <select
                      name="entity_type"
                      value={formData.entity_type}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      required
                    >
                      <option value="">Select Entity Type</option>
                      {ENTITY_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Principal Place of Business (Address) *
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="merchant_address"
                        value={formData.merchant_address}
                        onChange={handleChange}
                        placeholder="Full business address"
                        className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setToNA('merchant_address')}
                        className="px-3 py-2 text-xs font-medium text-slate-600 bg-slate-200 hover:bg-slate-300 rounded-lg"
                      >
                        N/A
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Processor Information (Optional) */}
              <div className="bg-slate-50 rounded-lg p-6">
                <h3 className="font-semibold text-slate-900 mb-4">2. PROCESSOR INFORMATION (Optional)</h3>
                <p className="text-sm text-slate-600 mb-4">If you have specific processor information, enter it here. This can be added later if not available.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div ref={processorInputRef} className="relative">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Bank/Processor Name
                    </label>
                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <input
                          type="text"
                          name="processor_name"
                          value={formData.processor_name}
                          onChange={handleProcessorChange}
                          onFocus={() => {
                            if (formData.processor_name.length >= 1) {
                              const filtered = getAllProcessors().filter(p => 
                                p.toLowerCase().includes(formData.processor_name.toLowerCase())
                              );
                              setProcessorSuggestions(filtered);
                              setShowProcessorDropdown(true);
                            } else {
                              setProcessorSuggestions(getAllProcessors().slice(0, 10));
                              setShowProcessorDropdown(true);
                            }
                          }}
                          placeholder="Start typing to search processors..."
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                          autoComplete="off"
                        />
                        {/* Autocomplete Dropdown */}
                        {showProcessorDropdown && (
                          <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {processorSuggestions.length > 0 ? (
                              <>
                                {processorSuggestions.slice(0, 10).map((processor, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    onClick={() => selectProcessor(processor)}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-purple-50 hover:text-purple-700 transition-colors border-b border-slate-100 last:border-0"
                                  >
                                    {processor}
                                  </button>
                                ))}
                                {processorSuggestions.length > 10 && (
                                  <div className="px-4 py-2 text-xs text-slate-500 bg-slate-50">
                                    + {processorSuggestions.length - 10} more results...
                                  </div>
                                )}
                              </>
                            ) : formData.processor_name.trim() ? (
                              <div className="p-3">
                                <p className="text-sm text-slate-600 mb-2">
                                  &ldquo;{formData.processor_name}&rdquo; not found in list
                                </p>
                                <button
                                  type="button"
                                  onClick={addCustomProcessor}
                                  className="w-full px-3 py-2 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                                >
                                  + Add &ldquo;{formData.processor_name}&rdquo; to list
                                </button>
                              </div>
                            ) : (
                              <div className="px-4 py-3 text-sm text-slate-500">
                                Type to search payment processors
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setToNA('processor_name')}
                        className="px-3 py-2 text-xs font-medium text-slate-600 bg-slate-200 hover:bg-slate-300 rounded-lg"
                      >
                        N/A
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Merchant ID Number(s)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="mid_numbers"
                        value={formData.mid_numbers}
                        onChange={handleChange}
                        placeholder="Enter MID number(s)"
                        className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => setToNA('mid_numbers')}
                        className="px-3 py-2 text-xs font-medium text-slate-600 bg-slate-200 hover:bg-slate-300 rounded-lg"
                      >
                        N/A
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Authorized Signer */}
              <div className="bg-slate-50 rounded-lg p-6">
                <h3 className="font-semibold text-slate-900 mb-4">3. AUTHORIZED SIGNER</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="signer_name"
                      value={formData.signer_name}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Title *
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="signer_title"
                        value={formData.signer_title}
                        onChange={handleChange}
                        placeholder="e.g., CEO, Owner, Managing Member"
                        className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setToNA('signer_title')}
                        className="px-3 py-2 text-xs font-medium text-slate-600 bg-slate-200 hover:bg-slate-300 rounded-lg"
                      >
                        N/A
                      </button>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="signer_email"
                      value={formData.signer_email}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={() => setStep(2)}
                  disabled={!isFormValid()}
                  className="primary-button px-8 py-3 disabled:opacity-50"
                >
                  Continue to Review & Sign →
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              {/* Document Preview */}
              <div className="border border-slate-200 rounded-lg p-6 bg-white">
                <div className="prose prose-sm max-w-none">
                  <p className="text-slate-700">
                    This Limited Power of Attorney (&ldquo;LPOA&rdquo;) is granted effective as of <strong>{getFormattedDate(formData.effective_date)}</strong> by <strong>{getFullMerchantName()}</strong>, a <strong>{formData.state_of_incorporation} {formData.entity_type}</strong> with its principal place of business at <strong>{formData.merchant_address}</strong> (hereinafter referred to as the &ldquo;Principal&rdquo;), to <strong>Reserve Release AI, LLC</strong>, a Wyoming limited liability company (hereinafter referred to as the &ldquo;Attorney-in-Fact&rdquo;).
                  </p>

                  <h4 className="font-bold mt-6">1. Grant of Authority</h4>
                  <p className="text-slate-700">
                    The Principal hereby appoints the Attorney-in-Fact to act in the Principal&apos;s name, place, and stead for the limited and specific purpose of managing, negotiating, and recovering funds held in reserve accounts by third-party financial institutions and payment processors.
                  </p>

                  <h4 className="font-bold mt-6">2. Enumerated Powers</h4>
                  <p className="text-slate-700">The Attorney-in-Fact shall have the following specific powers:</p>
                  <ul className="list-disc pl-6 text-slate-700">
                    <li><strong>Communication:</strong> To contact, correspond, and communicate with any Merchant Service Provider (MSP), Independent Sales Organization (ISO), acquiring bank, or payment processor (collectively, &ldquo;Processors&rdquo;) regarding the Principal&apos;s merchant accounts and reserve funds.</li>
                    <li><strong>Information Access:</strong> To request and receive account statements, reserve schedules, chargeback data, risk assessments, and any other documentation held by Processors related to the Principal&apos;s accounts.</li>
                    <li><strong>Negotiation:</strong> To negotiate the terms of release for any held funds, &ldquo;rolling reserves,&rdquo; or &ldquo;fixed reserves&rdquo; currently maintained by Processors.</li>
                    <li><strong>Settlement:</strong> To execute settlement agreements, release forms, and other documents necessary to finalize the release and transfer of reserve funds to the Principal or the Principal&apos;s designated account.</li>
                    <li><strong>Administrative Access:</strong> To access online portals or dashboards provided by Processors for the purpose of monitoring and managing reserve account status.</li>
                  </ul>

                  <h4 className="font-bold mt-6">3. Scope and Limitations</h4>
                  <p className="text-slate-700">This Power of Attorney is limited to the matters described above. The Attorney-in-Fact is not authorized to:</p>
                  <ul className="list-disc pl-6 text-slate-700">
                    <li>Bind the Principal to any new merchant processing agreements or debt obligations without separate written consent.</li>
                    <li>Withdraw funds for the Attorney-in-Fact&apos;s personal use, other than authorized fees as specified in separate service agreements.</li>
                    <li>Manage the Principal&apos;s general business operations or non-processing-related bank accounts.</li>
                  </ul>

                  <h4 className="font-bold mt-6">4. Term and Termination</h4>
                  <p className="text-slate-700">This LPOA shall remain in full force and effect until:</p>
                  <ul className="list-disc pl-6 text-slate-700">
                    <li>The completion of the reserve recovery services as outlined in the Engagement Agreement executed by and between the Parties (Principal and Attorney-in-Fact);</li>
                    <li>Written revocation by the Principal delivered to the agent for Attorney-in-Fact via certified mail (as defined and disclosed under the subject Engagement Agreement and or Terms of Service); or</li>
                    <li>Termination of the underlying Engagement Agreement between the Principal and Reserve Release AI, LLC.</li>
                  </ul>

                  <h4 className="font-bold mt-6">5. Indemnification and Reliance</h4>
                  <p className="text-slate-700">
                    The Principal hereby ratifies and confirms all lawful acts the Attorney-in-Fact shall do or cause to be done by virtue of this LPOA. All third parties, including but not limited to banks and processors, may rely on this LPOA as valid and are hereby indemnified by the Principal for any actions taken in good faith reliance upon the instructions of the Attorney-in-Fact within the scope of this LPOA.
                  </p>

                  <h4 className="font-bold mt-6">6. Governing Law</h4>
                  <p className="text-slate-700">
                    This Power of Attorney shall be governed by and construed in accordance with the laws of the State of Wyoming.
                  </p>
                </div>
              </div>

              {/* Draggable Signature Section */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <PenTool size={20} className="text-purple-600" />
                  Digital Signature - Principal (Merchant)
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Type Your Signature *
                    </label>
                    <input
                      type="text"
                      name="signature_text"
                      value={formData.signature_text}
                      onChange={handleChange}
                      placeholder="Type your full legal name as signature"
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none text-xl"
                    />
                  </div>

                  {/* Font Selection */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Signature Style</label>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(SIGNATURE_FONTS).map(([fontId, style]) => (
                        <button
                          key={fontId}
                          type="button"
                          onClick={() => setFormData({ ...formData, signature_font: fontId })}
                          className={`px-4 py-2 border rounded-lg transition-all ${
                            formData.signature_font === fontId 
                              ? 'border-purple-500 bg-purple-100' 
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <span style={style} className="text-slate-800">Aa</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Draggable Signature Preview */}
                  {formData.signature_text && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        <GripVertical size={16} className="inline mr-1" />
                        Drag your signature to the signature area below
                      </label>
                      <div 
                        ref={signatureRef}
                        draggable
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        className={`border-2 border-dashed rounded-lg p-4 bg-white cursor-move transition-all ${
                          isDragging ? 'border-purple-500 bg-purple-50 opacity-75' : 'border-slate-300 hover:border-purple-400'
                        }`}
                      >
                        <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                          <GripVertical size={14} />
                          <span>DRAG TO PLACE SIGNATURE</span>
                        </div>
                        <div 
                          style={{
                            ...SIGNATURE_FONTS[formData.signature_font],
                            color: '#00008B',
                          }}
                        >
                          {formData.signature_text}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Signature Drop Zone */}
                  <div 
                    className={`border-2 rounded-lg p-6 transition-all ${
                      signaturePosition.placed 
                        ? 'border-green-500 bg-green-50' 
                        : isDragging 
                          ? 'border-purple-500 bg-purple-50 border-dashed' 
                          : 'border-slate-200 bg-white'
                    }`}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    <p className="text-xs text-slate-500 mb-2">SIGNATURE AREA - Principal (Merchant):</p>
                    <p className="text-sm text-slate-600 mb-2"><strong>Name of Entity:</strong> {getFullMerchantName()}</p>
                    {signaturePosition.placed ? (
                      <div>
                        <p className="text-sm text-slate-600 mb-2"><strong>By:</strong></p>
                        <div 
                          style={{
                            ...SIGNATURE_FONTS[formData.signature_font],
                            color: '#00008B',
                            borderBottom: '2px solid #333',
                            paddingBottom: '8px',
                            display: 'inline-block',
                            minWidth: '200px'
                          }}
                        >
                          {formData.signature_text}
                        </div>
                        <div className="mt-2 text-sm text-slate-600">
                          <p><strong>Name:</strong> {formData.signer_name}</p>
                          <p><strong>Title:</strong> {formData.signer_title}</p>
                          <p className="text-xs text-slate-400">Date: {format(new Date(), 'MMMM d, yyyy')}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-400">
                        <PenTool size={32} className="mx-auto mb-2 opacity-50" />
                        <p>Drop your signature here</p>
                      </div>
                    )}
                  </div>

                  {/* Agreement Checkbox */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                        className="mt-1 w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm text-slate-700">
                        I, <strong>{formData.signer_name}</strong>, hereby acknowledge that my typed signature above 
                        constitutes my legal electronic signature. I have read and understand this Limited Power of Attorney 
                        and agree to its terms. I confirm I have the authority to sign on behalf of <strong>{getFullMerchantName()}</strong> as its authorized representative.
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 px-6 py-3 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
                >
                  ← Back to Edit
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading || !agreed || !formData.signature_text || !signaturePosition.placed}
                  className="flex-1 primary-button py-3 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? 'Submitting...' : (
                    <>
                      <PenTool size={20} />
                      Sign & Submit LPOA
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-green-600" size={40} />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">LPOA Submitted Successfully!</h3>
              <p className="text-slate-600 mb-6">
                Your Limited Power of Attorney has been submitted and is pending admin review and counter-signature.
              </p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6 text-left max-w-lg mx-auto">
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <AlertCircle size={18} className="text-blue-600" />
                  What happens next?
                </h4>
                <ol className="list-decimal pl-5 space-y-2 text-sm text-slate-700">
                  <li>Our team will review your LPOA document</li>
                  <li>An authorized representative will counter-sign the agreement</li>
                  <li>The fully executed LPOA will be uploaded to your Documents section</li>
                  <li>A copy will be securely stored in Dropbox</li>
                  <li>We can then proceed with your reserve recovery case</li>
                </ol>
              </div>

              <button
                onClick={onClose}
                className="primary-button px-8 py-3"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LimitedPOAForm;
